import {
  format,
  isSameDay,
  startOfDay,
  setDefaultOptions,
  intervalToDuration,
  Duration as DateFnsDuration,
} from "date-fns";
import { Duration } from "luxon";
import i18n from "i18next";

//TimeAgo and ReactTimeAgo libs
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
import fr from "javascript-time-ago/locale/fr";
import pl from "javascript-time-ago/locale/pl";
import { LocaleUtil } from "@utils/locale.util";

const TimeAgoLocales = new Map();

[en, fr, pl].forEach((locale) => {
  TimeAgo.addLocale(locale);
  TimeAgoLocales.set(locale.locale, locale);
});

TimeAgo.addDefaultLocale(en);

i18n.on("languageChanged", () => {
  TimeAgo.setDefaultLocale(getTimeAgoLocale());
  setDefaultOptions({ locale: LocaleUtil.getDateFnsLocale() });
});

const DEFAULT_TIME_FORMAT = "p";
const DEFAULT_DATE_FORMAT = "P";
const DEFAULT_DATE_TIME_FORMAT = "Pp";

const MS_IN_SECOND = 1000;
const MS_IN_MINUTE = 60 * MS_IN_SECOND;
const MS_IN_HOUR = 60 * MS_IN_MINUTE;
const MS_IN_DAY = 24 * MS_IN_HOUR;

function getTimeAgoLocale(): string {
  if (!i18n.language) {
    return en.locale;
  }

  return (
    TimeAgoLocales.get(
      LocaleUtil.getRootLanguageCodeFromLocaleString(i18n.language),
    )?.locale || en.locale
  );
}

setDefaultOptions({ locale: LocaleUtil.getDateFnsLocale() });

export class DateUtil {
  static formatTime(date: Date | number | string): string {
    return format(date, DEFAULT_TIME_FORMAT, { locale: LocaleUtil.getDateFnsLocale() });
  }

  static formatDate(date: Date | number | string, formatStr?: string): string {
    return format(date, formatStr || DEFAULT_DATE_FORMAT, {
      locale: LocaleUtil.getDateFnsLocale(),
    });
  }

  static formatDateTime(date: Date | number | string): string {
    return format(date, DEFAULT_DATE_TIME_FORMAT, {
      locale: LocaleUtil.getDateFnsLocale(),
    });
  }

  static isSameDay(
    date1: Date | number | string,
    date2: Date | number | string,
  ): boolean {
    return isSameDay(date1, date2);
  }

  static startOfDay(date: Date | number | string): Date | number | string {
    return startOfDay(date);
  }

  static getDaysAgo(date: Date | number | string): number {
    const today = new Date();

    const timeDifference = today.getTime() - new Date(date).getTime();

    return Math.floor(timeDifference / MS_IN_DAY);
  }

  static formatDuration(millis: number): string {
    const obj =
      millis / MS_IN_MINUTE > 1
        ? { minute: Math.ceil(millis / MS_IN_MINUTE) }
        : { second: Math.ceil(millis / MS_IN_SECOND) };
    return Duration.fromObject(obj, {
      locale: i18n.language,
    }).toHuman({ listStyle: "short" });
  }

  static getDiff(startDate: Date, endDate: Date): DateFnsDuration {
    return intervalToDuration({ start: startDate, end: endDate });
  }
}
